import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import InputAdornment from "@mui/material/InputAdornment";

import ResponsiveGrid from "../grid/responsiveGrid";

import { GridActionsCellItem, GridToolbarContainer } from "@mui/x-data-grid";
import { CustomPagination } from "../customPagination";
import { ConvertISOToDate } from "../util";

import { useUserAuth } from "../contexts/authContext";

function EditToolbar({ setEditMonitor }) {
    const handleAddButtonClick = () => {
        const defaultMonitorValues = {
            id: 0, // must be 0 when creating a new record
            monitor_uuid: uuidv4(),
            monitor_name: "",
            monitor_type: "LATENCY",
            source_type: "ADS_B",
            nominal_threshold: 1,
            nominal_fault_code: 210,
            low_threshold: 1.5,
            low_fault_code: 211,
            medium_threshold: 2,
            medium_fault_code: 212,
            high_threshold: 100,
            high_fault_code: 213
        };
        setEditMonitor(defaultMonitorValues);
    };
    return (
        <GridToolbarContainer>
            <Button color="primary" startIcon={<AddIcon />} onClick={handleAddButtonClick}>
                Add Record
            </Button>
        </GridToolbarContainer>
    );
}

function HmsMonitorDialog({ editMonitor, setEditMonitor, getHimsMonitors }) {
    const [monitorName, setMonitorName] = useState("");
    const [monitorNameErrorText, setMonitorNameErrorText] = useState("");

    const [monitorType, setMonitorType] = useState("");
    const [sourceType, setSourceType] = useState("");
    const [monitorId, setMonitorId] = useState(0);

    const [monitorUuid, setMonitorUuid] = useState("");

    const [nominalThreshold, setNominalThreshold] = useState(0);
    const [nominalThresholdErrorText, setNominalThresholdErrorText] = useState("");

    const [nominalFaultCode, setNominalFaultCode] = useState(0);
    const [nominalFaultCodeErrorText, setNominalFaultCodeErrorText] = useState("");

    const [lowThreshold, setLowThreshold] = useState(0);
    const [lowThresholdErrorText, setLowThresholdErrorText] = useState("");

    const [lowFaultCode, setLowFaultCode] = useState(0);
    const [lowFaultCodeErrorText, setLowFaultCodeErrorText] = useState("");

    const [mediumThreshold, setMediumThreshold] = useState(0);
    const [mediumThresholdErrorText, setMediumThresholdErrorText] = useState("");

    const [mediumFaultCode, setMediumFaultCode] = useState(0);
    const [mediumFaultCodeErrorText, setMediumFaultCodeErrorText] = useState("");

    const [highThreshold, setHighThreshold] = useState(0);
    const [highThresholdErrorText, setHighThresholdErrorText] = useState("");

    const [highFaultCode, setHighFaultCode] = useState(0);
    const [highFaultCodeErrorText, setHighFaultCodeErrorText] = useState("");

    const { handleFailedFetch } = useUserAuth();

    useEffect(() => {
        initializeHimsMonitorFields(editMonitor);
    }, []);

    const initializeHimsMonitorFields = (monitor) => {
        setMonitorId(monitor.id);
        setMonitorUuid(monitor.monitor_uuid);

        setMonitorName(monitor.monitor_name);
        setMonitorType(monitor.monitor_type);
        setSourceType(monitor.source_type);

        setNominalThreshold(monitor.nominal_threshold);
        setNominalFaultCode(monitor.nominal_fault_code);

        setLowThreshold(monitor.low_threshold);
        setLowFaultCode(monitor.low_fault_code);

        setMediumThreshold(monitor.medium_threshold);
        setMediumFaultCode(monitor.medium_fault_code);

        setHighThreshold(monitor.high_threshold);
        setHighFaultCode(monitor.high_fault_code);
    };
    const handleSaveButtonClick = () => {
        let allFieldsAreValid = true;
        if (typeof monitorName !== "string" || monitorName.trim() === "") {
            setMonitorNameErrorText("Please enter a name.");
            allFieldsAreValid = false;
        } else if (monitorNameErrorText) {
            setMonitorNameErrorText("");
        }
        const values = [
            {
                value: nominalThreshold,
                errorText: nominalThresholdErrorText,
                setErrorText: setNominalThresholdErrorText
            },
            {
                value: nominalFaultCode,
                errorText: nominalFaultCodeErrorText,
                setErrorText: setNominalFaultCodeErrorText
            },
            {
                value: lowThreshold,
                errorText: lowThresholdErrorText,
                setErrorText: setLowThresholdErrorText
            },
            {
                value: lowFaultCode,
                errorText: lowFaultCodeErrorText,
                setErrorText: setLowFaultCodeErrorText
            },
            {
                value: mediumThreshold,
                errorText: mediumThresholdErrorText,
                setErrorText: setMediumThresholdErrorText
            },
            {
                value: mediumFaultCode,
                errorText: mediumFaultCodeErrorText,
                setErrorText: setMediumFaultCodeErrorText
            },
            {
                value: highThreshold,
                errorText: highThresholdErrorText,
                setErrorText: setHighThresholdErrorText
            },
            {
                value: highFaultCode,
                errorText: highFaultCodeErrorText,
                setErrorText: setHighFaultCodeErrorText
            }
        ];
        values.forEach(({ value, errorText, setErrorText }) => {
            const valueFloat = parseFloat(value);
            if (isNaN(valueFloat) || valueFloat <= 0) {
                if (allFieldsAreValid === true) {
                    allFieldsAreValid = false;
                }
                setErrorText("Please enter an integer greater than 0.");
            } else if (errorText) {
                setErrorText("");
            }
        });
        if (allFieldsAreValid === false) {
            return;
        }
        const monitor = {
            id: monitorId,
            monitor_uuid: monitorUuid,
            monitor_name: monitorName,
            monitor_type: monitorType,
            source_type: sourceType,
            nominal_threshold: nominalThreshold,
            nominal_fault_code: nominalFaultCode,
            low_threshold: lowThreshold,
            low_fault_code: lowFaultCode,
            medium_threshold: mediumThreshold,
            medium_fault_code: mediumFaultCode,
            high_threshold: highThreshold,
            high_fault_code: highFaultCode
        };
        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(monitor)
        };
        fetch("/api/hms/monitors/insertupdate", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then(() => {
                setEditMonitor(null);
                getHimsMonitors();
            })
            .catch((err) => {
                handleFailedFetch(err);
            });
    };
    const handleSourceTypeChange = (e) => {
        const newSourceType = e.target.value;
        setSourceType(newSourceType);
        handleSetDefaultThresholdValues(monitorType, newSourceType);
    };
    const handleMonitorTypeChange = (e) => {
        const newMonitorType = e.target.value;
        setMonitorType(newMonitorType);
        handleSetDefaultThresholdValues(newMonitorType, sourceType);
    };
    const handleSetDefaultThresholdValues = (newMonitorType, newSourceType) => {
        if (newMonitorType === "LATENCY" && newSourceType === "ADS_B") {
            setNominalThreshold(1);
            setLowThreshold(1.5);
            setMediumThreshold(2);
            setHighThreshold(100);
        } else if (newMonitorType === "LATENCY" && newSourceType === "GROUND_BASED_RADAR") {
            setNominalThreshold(2);
            setLowThreshold(3);
            setMediumThreshold(4);
            setHighThreshold(200);
        } else if (newMonitorType === "SATURATION" && newSourceType === "ADS_B") {
            setNominalThreshold(1000);
            setLowThreshold(1500);
            setMediumThreshold(2000);
            setHighThreshold(100000);
        } else if (newMonitorType === "SATURATION" && newSourceType === "GROUND_BASED_RADAR") {
            setNominalThreshold(400);
            setLowThreshold(800);
            setMediumThreshold(1200);
            setHighThreshold(40000);
        }
    };
    return (
        <Dialog onClose={() => setEditMonitor(null)} open={editMonitor ? true : false} maxWidth="sm" fullWidth>
            <DialogTitle>Configure HIMS Monitor</DialogTitle>
            <DialogContent sx={{ display: "flex", flexDirection: "column", gap: "12px", pt: "8px !important" }}>
                <TextField
                    value={monitorName}
                    onChange={(e) => setMonitorName(e.target.value)}
                    label="Monitor Name"
                    fullWidth
                    error={monitorNameErrorText.length > 0 ? true : false}
                    helperText={monitorNameErrorText}
                />

                <FormControl fullWidth>
                    <InputLabel id="monitor-type-select-label">Monitor Type</InputLabel>
                    <Select labelId="monitor-type-select-label" value={monitorType} label="Monitor Type" onChange={handleMonitorTypeChange}>
                        <MenuItem value={"LATENCY"}>LATENCY</MenuItem>
                        <MenuItem value={"SATURATION"}>SATURATION</MenuItem>
                    </Select>
                </FormControl>

                <FormControl fullWidth>
                    <InputLabel id="source-type-select-label">Source Type</InputLabel>
                    <Select labelId="source-type-select-label" value={sourceType} label="Source Type" onChange={handleSourceTypeChange}>
                        <MenuItem value={"ADS_B"}>ADS_B</MenuItem>
                        <MenuItem value={"GROUND_BASED_RADAR"}>GROUND_BASED_RADAR</MenuItem>
                    </Select>
                </FormControl>

                <Divider textAlign="left" flexItem>
                    Nominal Fault Level
                </Divider>
                <Box sx={{ display: "flex", flexDirection: "row", gap: 1, justifyContent: "center", alignItems: "center" }}>
                    <TextField
                        value={nominalThreshold}
                        onChange={(e) => setNominalThreshold(e.target.value)}
                        label="Threshold"
                        fullWidth
                        error={nominalThresholdErrorText.length > 0 ? true : false}
                        helperText={nominalThresholdErrorText}
                        slotProps={{
                            input: {
                                endAdornment: <InputAdornment position="end">{monitorType === "LATENCY" ? "seconds" : "detections per update"}</InputAdornment>
                            }
                        }}
                    />
                    <TextField
                        value={nominalFaultCode}
                        onChange={(e) => setNominalFaultCode(e.target.value)}
                        label="Fault Code"
                        fullWidth
                        error={nominalFaultCodeErrorText.length > 0 ? true : false}
                        helperText={nominalFaultCodeErrorText}
                    />
                </Box>

                <Divider textAlign="left" flexItem>
                    Low Fault Level
                </Divider>
                <Box sx={{ display: "flex", flexDirection: "row", gap: 1, justifyContent: "center", alignItems: "center" }}>
                    <TextField
                        value={lowThreshold}
                        onChange={(e) => setLowThreshold(e.target.value)}
                        label="Threshold"
                        fullWidth
                        error={lowThresholdErrorText.length > 0 ? true : false}
                        helperText={lowThresholdErrorText}
                        slotProps={{
                            input: {
                                endAdornment: <InputAdornment position="end">{monitorType === "LATENCY" ? "seconds" : "detections per update"}</InputAdornment>
                            }
                        }}
                    />
                    <TextField
                        value={lowFaultCode}
                        onChange={(e) => setLowFaultCode(e.target.value)}
                        label="Fault Code"
                        fullWidth
                        error={lowFaultCodeErrorText.length > 0 ? true : false}
                        helperText={lowFaultCodeErrorText}
                    />
                </Box>

                <Divider textAlign="left" flexItem>
                    Medium Fault Level
                </Divider>
                <Box sx={{ display: "flex", flexDirection: "row", gap: 1, justifyContent: "center", alignItems: "center" }}>
                    <TextField
                        value={mediumThreshold}
                        onChange={(e) => setMediumThreshold(e.target.value)}
                        label="Threshold"
                        fullWidth
                        error={mediumThresholdErrorText.length > 0 ? true : false}
                        helperText={mediumThresholdErrorText}
                        slotProps={{
                            input: {
                                endAdornment: <InputAdornment position="end">{monitorType === "LATENCY" ? "seconds" : "detections per update"}</InputAdornment>
                            }
                        }}
                    />
                    <TextField
                        value={mediumFaultCode}
                        onChange={(e) => setMediumFaultCode(e.target.value)}
                        label="Fault Code"
                        fullWidth
                        error={mediumFaultCodeErrorText.length > 0 ? true : false}
                        helperText={mediumFaultCodeErrorText}
                    />
                </Box>

                <Divider textAlign="left" flexItem>
                    High Fault Level
                </Divider>
                <Box sx={{ display: "flex", flexDirection: "row", gap: 1, justifyContent: "center", alignItems: "center" }}>
                    <TextField
                        value={highThreshold}
                        onChange={(e) => setHighThreshold(e.target.value)}
                        label="Threshold"
                        fullWidth
                        error={highThresholdErrorText.length > 0 ? true : false}
                        helperText={highThresholdErrorText}
                        slotProps={{
                            input: {
                                endAdornment: <InputAdornment position="end">{monitorType === "LATENCY" ? "seconds" : "detections per update"}</InputAdornment>
                            }
                        }}
                    />
                    <TextField
                        value={highFaultCode}
                        onChange={(e) => setHighFaultCode(e.target.value)}
                        label="Fault Code"
                        fullWidth
                        error={highFaultCodeErrorText.length > 0 ? true : false}
                        helperText={highFaultCodeErrorText}
                    />
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={() => setEditMonitor(null)}>Close</Button>
                <Button onClick={handleSaveButtonClick}>Save</Button>
            </DialogActions>
        </Dialog>
    );
}

function HmsMonitorSettings() {
    const [allHimsMonitors, setAllHimsMonitors] = useState([]);
    const [editMonitor, setEditMonitor] = useState(null);
    const [sortModel, setSortModel] = useState([{ field: "date_updated", sort: "desc" }]);

    const { handleFailedFetch } = useUserAuth();

    useEffect(() => {
        getHimsMonitors();

        return () => {
            setAllHimsMonitors([]);
        };
    }, []);

    const getHimsMonitors = () => {
        const requestOptions = {
            method: "GET",
            headers: { "Content-Type": "application/json" }
        };
        fetch("/api/hms/monitors/get", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then((data) => setAllHimsMonitors(data))
            .catch((err) => handleFailedFetch(err));
    };

    const handleDeleteMonitorButtonClick = (monitor) => {
        if (confirm("Are you sure you would like to delete this monitor? This action cannot be undone.") === false) {
            return;
        }
        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(monitor)
        };
        fetch("/api/hms/monitors/delete", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then(() => {
                getHimsMonitors();
            })
            .catch((err) => {
                handleFailedFetch(err);
            });
    };

    const columns = [
        {
            field: "monitor_name",
            headerName: "Monitor Name",
            minWidth: 100,
            editable: false,
            flex: 1
        },
        {
            field: "monitor_type",
            headerName: "Monitor Type",
            minWidth: 50,
            editable: false,
            flex: 1
        },
        {
            field: "monitor_uuid",
            headerName: "Monitor UUID",
            minWidth: 300,
            editable: false,
            flex: 1
        },
        {
            field: "source_type",
            headerName: "Source Type",
            minWidth: 100,
            editable: false,
            flex: 1
        },
        {
            field: "date_created",
            headerName: "Date Created",
            minWidth: 100,
            editable: false,
            flex: 1,
            valueGetter: (value) => `${ConvertISOToDate(value)}`
        },
        {
            field: "date_updated",
            headerName: "Date Updated",
            minWidth: 100,
            editable: false,
            flex: 1,
            valueGetter: (value) => `${ConvertISOToDate(value)}`
        },
        {
            field: "actions",
            type: "actions",
            headerName: "Actions",
            minWidth: 50,
            getActions: ({ row }) => {
                return [
                    <GridActionsCellItem key={"1"} icon={<EditIcon />} label="Edit" onClick={() => setEditMonitor(row)} color="inherit" />,
                    <GridActionsCellItem key={"2"} icon={<DeleteIcon />} label="Delete" onClick={() => handleDeleteMonitorButtonClick(row)} color="inherit" />
                ];
            }
        }
    ];
    return (
        <Box sx={{ height: 567 }}>
            <ResponsiveGrid
                primaryColumns={["monitor_name", "actions"]}
                rows={allHimsMonitors}
                columns={columns}
                disableRowSelectionOnClick
                disableVirtualization
                slots={{ toolbar: EditToolbar, pagination: CustomPagination }}
                slotProps={{ toolbar: { setEditMonitor } }}
                initialState={{ pagination: { paginationModel: { pageSize: 7 } } }}
                sortModel={sortModel}
                onSortModelChange={(model) => setSortModel(model)}
            />

            {editMonitor ? <HmsMonitorDialog editMonitor={editMonitor} setEditMonitor={setEditMonitor} getHimsMonitors={getHimsMonitors} /> : <></>}
        </Box>
    );
}

export default HmsMonitorSettings;
